import clsx from "clsx"
import {
  ArenaActiveDuel,
  ArenaMatchEvent,
  ArenaMatchFinishedPayload,
  ArenaMatchPlayer,
  ArenaSlotNumber,
  ArenaTeamId,
} from "@/types/arena"
import { BattleCardView, CardElement } from "@/types/battle"
import { formatArenaEventLogLine } from "@/utils/arenaNormalize"
import { formatCountdown, useDeadlineCountdown } from "@/features/arena/useArenaCountdown"
import { displayCardBS } from "@/constants/game.constants"
import { ELEMENT_NAMES, STYLE_ICONS } from "@/constants/guardians"
import { resolveCardHeroImage } from "@/constants/cardAssets"
import { formatPointsDelta } from "@/constants/game.constants"
import AbilityPicker from "@/features/duel/components/AbilityPicker"

const SLOTS: ArenaSlotNumber[] = [1, 2, 3]

export function ArenaLobbyPanel({
  onlineCount,
  queueSize,
  needed,
  queueMode,
  onJoinQueue,
  onCancelQueue,
  busy,
}: {
  onlineCount: number
  queueSize: number
  needed: number
  queueMode: "idle" | "queue" | "capacity"
  onJoinQueue: () => void
  onCancelQueue: () => void
  busy: boolean
}) {
  return (
    <div className="space-y-4 text-sm">
      <p className="text-guardians-muted">Онлайн в арене: {onlineCount}</p>
      {queueMode === "queue" ? (
        <p className="text-guardians-accent">
          В очереди {queueSize} / нужно {needed}
        </p>
      ) : null}
      {queueMode === "capacity" ? (
        <p className="text-amber-200">Ожидание места на арене…</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy || queueMode === "queue"}
          onClick={onJoinQueue}
          className="rounded-xl bg-guardians-accent px-4 py-2 text-guardians-bg disabled:opacity-50"
        >
          В очередь
        </button>
        <button
          type="button"
          disabled={busy || queueMode === "idle"}
          onClick={onCancelQueue}
          className="rounded-xl border border-white/20 px-4 py-2"
        >
          Выйти из очереди
        </button>
      </div>
    </div>
  )
}

export function ArenaSlotsPanel({
  team,
  players,
  myUserId,
  slotDeadlineAt,
  busy,
  onClaimSlot,
  onForfeit,
}: {
  team: ArenaTeamId | null
  players: ArenaMatchPlayer[]
  myUserId: number | null
  slotDeadlineAt: string | null
  busy: boolean
  onClaimSlot: (slot: ArenaSlotNumber) => void
  onForfeit: () => void
}) {
  const remainingMs = useDeadlineCountdown(slotDeadlineAt)
  const allies = players.filter((p) => (team ? p.team === team : false))
  const rivals = players.filter((p) => (team ? p.team !== team : true))
  const mySlot = players.find((p) => p.userId === myUserId)?.slot ?? null

  const occupant = (slot: ArenaSlotNumber) =>
    allies.find((p) => p.slot === slot) ?? null

  return (
    <div className="space-y-4 text-sm">
      <p className="font-medium">Выбор слота</p>
      <p className="text-xs text-guardians-muted">
        Команда {team ?? "—"} · {formatCountdown(remainingMs)}
      </p>
      <div className="grid grid-cols-3 gap-2">
        {SLOTS.map((slot) => {
          const taken = occupant(slot)
          const isMine = mySlot === slot
          const disabled = busy || (taken != null && !isMine) || mySlot != null
          return (
            <button
              key={slot}
              type="button"
              disabled={disabled || isMine}
              onClick={() => onClaimSlot(slot)}
              className={clsx(
                "rounded-xl border p-3 text-left",
                isMine && "border-guardians-accent bg-guardians-accent/10",
                taken && !isMine && "opacity-60",
                !taken && "border-white/15"
              )}
            >
              <span className="text-lg font-bold">{slot}</span>
              <p className="mt-1 truncate text-xs">
                {taken ? taken.nickname : "Свободно"}
              </p>
            </button>
          )
        })}
      </div>
      <TeamList title="Союзники" players={allies} />
      <TeamList title="Соперники" players={rivals} />
      <button
        type="button"
        onClick={onForfeit}
        className="text-xs text-red-300 underline"
      >
        Сдаться
      </button>
    </div>
  )
}

function TeamList({ title, players }: { title: string; players: ArenaMatchPlayer[] }) {
  return (
    <div>
      <p className="mb-1 text-xs text-guardians-muted">{title}</p>
      <ul className="space-y-1 text-xs">
        {players.map((p) => (
          <li key={p.userId} className="flex justify-between">
            <span>{p.nickname}</span>
            <span className="text-guardians-muted">
              слот {p.slot ?? "—"} · {p.state ?? "OK"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function ArenaCombatPanel({
  duel,
  inActiveDuel,
  cards,
  burnedCardIds,
  selectedCardId,
  selectedAbilityId,
  affiliationElement,
  guardianRank,
  log,
  busy,
  onSelectCard,
  onSelectAbility,
  onForfeit,
}: {
  duel: ArenaActiveDuel | null
  inActiveDuel: boolean
  cards: BattleCardView[]
  burnedCardIds: string[]
  selectedCardId: string | null
  selectedAbilityId: string | null
  affiliationElement: CardElement | null | undefined
  guardianRank: number
  log: ArenaMatchEvent[]
  busy: boolean
  onSelectCard: (cardId: string, abilityId?: string | null) => void
  onSelectAbility: (abilityId: string | null) => void
  onForfeit: () => void
}) {
  const remainingMs = useDeadlineCountdown(duel?.cardDeadlineAt)
  const pickable = cards.filter((c) => !burnedCardIds.includes(c.id))
  const canPick =
    inActiveDuel && !busy && !selectedCardId && duel?.yourTurn !== false

  return (
    <div className="space-y-4 text-sm">
      <div>
        <p className="font-medium">{inActiveDuel ? "Ваша дуэль" : "Скамейка"}</p>
        {duel ? (
          <p className="text-xs text-guardians-muted">
            {duel.teamANickname} vs {duel.teamBNickname}
            {inActiveDuel ? ` · ${formatCountdown(remainingMs)}` : ""}
          </p>
        ) : (
          <p className="text-xs text-guardians-muted">Ожидание дуэли…</p>
        )}
      </div>

      {canPick ? (
        <>
        <AbilityPicker
          affiliationElement={affiliationElement}
          guardianRank={guardianRank}
          selectedAbilityId={selectedAbilityId}
          onSelect={onSelectAbility}
          disabled={busy}
        />
        <div className="grid grid-cols-2 gap-2">
          {pickable.map((card) => {
            const hero = resolveCardHeroImage(card)
            const src = typeof hero === "string" ? hero : ""
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => onSelectCard(card.id, selectedAbilityId)}
                className="overflow-hidden rounded-lg border border-white/10 text-left"
              >
                {src ? (
                  <img src={src} alt="" className="aspect-[4/3] w-full object-cover" />
                ) : null}
                <div className="p-1.5 text-[10px]">
                  {ELEMENT_NAMES[card.element]} {STYLE_ICONS[card.style]} BS{" "}
                  {displayCardBS(card)}
                </div>
              </button>
            )
          })}
        </div>
        </>
      ) : null}

      {inActiveDuel && selectedCardId ? (
        <p className="text-guardians-accent">Карта выбрана — ждём соперника…</p>
      ) : null}

      {burnedCardIds.length > 0 ? (
        <p className="text-xs text-guardians-muted">
          Burn: {burnedCardIds.length} карт недоступны
        </p>
      ) : null}

      <div className="max-h-40 overflow-y-auto rounded-lg border border-white/10 bg-black/30 p-2 text-[10px]">
        <p className="mb-1 font-medium">Лог</p>
        {[...log].reverse().map((event) => (
          <p key={`${event.seq}-${event.eventType}`} className="text-guardians-muted">
            {formatArenaEventLogLine(event)}
          </p>
        ))}
      </div>

      <button type="button" onClick={onForfeit} className="text-xs text-red-300 underline">
        Сдаться
      </button>
    </div>
  )
}

export function ArenaResultPanel({
  finished,
  myTeam,
  onAgain,
}: {
  finished: ArenaMatchFinishedPayload
  myTeam: ArenaTeamId | null
  onAgain: () => void
}) {
  const won =
    finished.winningTeam != null &&
    myTeam != null &&
    finished.winningTeam === myTeam
  const draw = finished.winningTeam == null || finished.outcome === "DRAW"
  const title = draw ? "Ничья" : won ? "Победа" : "Поражение"
  const { ptsDelta, coinsDelta, dustDelta } = finished.rewards

  return (
    <div className="space-y-4 text-center text-sm">
      <p className="text-xl font-bold text-guardians-accent">{title}</p>
      {finished.winningTeam ? (
        <p className="text-guardians-muted">Победила команда {finished.winningTeam}</p>
      ) : null}
      <div className="space-y-1">
        <p>{formatPointsDelta(ptsDelta)}</p>
        <p>
          {coinsDelta >= 0 ? "+" : ""}
          {coinsDelta} монет
        </p>
        {dustDelta !== 0 ? (
          <p>
            {dustDelta >= 0 ? "+" : ""}
            {dustDelta} пыли
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onAgain}
        className="rounded-xl bg-guardians-accent px-6 py-2 text-guardians-bg"
      >
        В лобби арены
      </button>
    </div>
  )
}
